import { useCallback, useEffect } from "react";
import { Dictionary, groupBy } from "lodash";
import { create } from "zustand";
import { PageableOf } from "@components/table/_components/pageable/_types/pageable.types";
import useFiltersApi from "@services/api/filter/filter.api";
import { FilterProps } from "@services/api/filter/filter.types";

export function getNumbers(filter: FilterProps, defaultValues: number[]) {
  if (filter.values) {
    return filter.values
      .split(",")
      .map((val) => parseFloat(val.trim()))
      .filter((val) => !isNaN(val));
  }

  return defaultValues;
}

interface FilterStoreProps {
  filters: Dictionary<FilterProps[]>;
  setFilters: (filter: PageableOf<FilterProps>) => void;
}

export const useFilterStore = create<FilterStoreProps>((set) => ({
  filters: {},
  setFilters: (filters: PageableOf<FilterProps>) =>
    set((state) => ({
      filters: { ...state.filters, ...groupBy(filters.content, "entityType") }
    }))
}));

export default function useFilter() {
  const { usePageQuery } = useFiltersApi();
  const { data: fetchedFilters } = usePageQuery({
    variables: {
      size: 9999,
      sort: ["sequence,ASC", "name,ASC"]
    }
  });

  const { filters, setFilters } = useFilterStore();
  useEffect(() => {
    if (!fetchedFilters) return;
    setFilters(fetchedFilters);
  }, [fetchedFilters, setFilters]);

  const getFilters = useCallback(
    (type: string) => {
      if (!filters || !filters[type]) return [];
      return filters[type];
    },
    [filters]
  );

  const getVisibleFilters = useCallback(
    (type: string) => {
      const filters = getFilters(type);
      return filters.filter((filter) => filter.visible);
    },
    [getFilters]
  );

  return {
    filters,
    setFilters,
    getFilters,
    getVisibleFilters
  };
}
